import getConfig from "../config";
import type { Feature } from "./types";
import { useInternalUserValidation, useIsEligible } from "./useAccessControl";
import { useSessionCheck } from "./useSessionCheck";
import { useSettingsValidation } from "./useSettingsValidation";

/**
 * Custom hook to determine if a feature should be enabled based on permissions and settings
 * @param {Feature} feature - Object containing permission and setting properties
 * @param {boolean} [viewOnly=false] - Flag to indicate if the feature is for view-only access
 * @returns {boolean} - Returns true if the user has permission and the feature is enabled in settings
 * @example
 * const feature = {
 *   permission: "CAN_USE_FEATURE_X",
 *   setting: "FEATURE_X_ENABLED"
 * };
 * const isAuthorized = useIsAuthorized(feature);
 */
export const useIsAuthorized = (feature: Feature, viewOnly: boolean = false): boolean => {
    // Get config lazily to support runtime configuration
    const appEnv = getConfig().appEnv;

    // Handle setting property - exists in FeatureFlagConfig but not AccessConfig
    const setting: string = "setting" in feature ? (feature.setting ?? "") : "";

    // Handle internalUser property - exists in both types.
    // When internalUserEnv is provided, the internal-user restriction applies only
    // in the listed environments; elsewhere the feature is open to all eligible users.
    const internalUserEnv = "internalUserEnv" in feature ? feature.internalUserEnv : undefined;
    const internalUser = internalUserEnv
        ? internalUserEnv.includes(appEnv as string) || null
        : (feature.internalUser ?? null);

    const isSettingEnabled = useSettingsValidation(setting);

    const isInternalUserEnabled = useInternalUserValidation(internalUser);

    // Check if feature should be hidden in current environment
    const isHiddenEnv = feature.env ? !feature.env.includes(appEnv as string) : false;

    const isEligible = useIsEligible(feature, viewOnly);

    const isSessionCheckAllowed = useSessionCheck(
        "sessionCheck" in feature ? feature.sessionCheck : undefined
    );
    return (
        !isHiddenEnv &&
        isSettingEnabled &&
        isInternalUserEnabled &&
        isEligible &&
        isSessionCheckAllowed
    );
};
